/**
 * TemporalEchoStateSystem — snapshot and rewind state for Temporal Echo.
 *
 * Temporal Echo (temporal_echo) records the player's position and HP at
 * activation, then allows reverting to that snapshot within a configurable
 * window. The revert is clean: damage is undone and position is restored
 * without triggering death events or other side effects.
 *
 * Responsibilities:
 * - beginRecording(): stamp position and HP.
 * - update(): countdown the recording window, auto-expire when it elapses.
 * - revert(): restore position and HP from snapshot, clear the snapshot.
 * - expire(): discard without reverting (window elapsed, player chose not to revert).
 *
 * The actual entity state mutation (moving the player, setting HP) is
 * performed via the injected EntityStateWriter, keeping this module free of
 * Phaser Body / character stats APIs.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § TemporalEchoStateSystem
 * @see ADR-064 (attunement shared systems architecture)
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TemporalEchoSnapshot {
  x: number;
  y: number;
  hp: number;
  timestamp: number;
}

/** Minimal write interface for committing a revert. */
export interface TemporalEchoStateWriter {
  setPosition(x: number, y: number): void;
  setHp(hp: number): void;
}

// ---------------------------------------------------------------------------
// System
// ---------------------------------------------------------------------------

export class TemporalEchoStateSystem {
  private _snapshot: TemporalEchoSnapshot | null = null;
  private _windowMs = 0;
  private _remainingMs = 0;
  private readonly _writer: TemporalEchoStateWriter;

  constructor(writer: TemporalEchoStateWriter) {
    this._writer = writer;
  }

  // ---------------------------------------------------------------------------
  // Recording
  // ---------------------------------------------------------------------------

  /**
   * Begin recording. Stamps current position and HP as the revert point.
   * @param x         Player's current world x.
   * @param y         Player's current world y.
   * @param hp        Player's current HP.
   * @param windowMs  How long the revert window stays open (ms).
   */
  beginRecording(x: number, y: number, hp: number, windowMs: number): void {
    this._snapshot = { x, y, hp, timestamp: Date.now() };
    this._windowMs = windowMs;
    this._remainingMs = windowMs;
  }

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  /** Whether a valid snapshot exists and the window is still open. */
  get isRecording(): boolean {
    return this._snapshot !== null && this._remainingMs > 0;
  }

  /** Remaining window duration in ms. 0 if not recording. */
  get remainingWindowMs(): number {
    return this._remainingMs;
  }

  /** The active snapshot, or null if none. */
  get snapshot(): Readonly<TemporalEchoSnapshot> | null {
    return this._snapshot;
  }

  // ---------------------------------------------------------------------------
  // Revert / expire
  // ---------------------------------------------------------------------------

  /**
   * Revert the player to the stored snapshot.
   * @returns true if a snapshot existed and was applied; false if nothing to revert.
   */
  revert(): boolean {
    if (!this._snapshot) return false;
    const snap = this._snapshot;
    this._snapshot = null;
    this._remainingMs = 0;
    this._writer.setPosition(snap.x, snap.y);
    this._writer.setHp(snap.hp);
    return true;
  }

  /**
   * Discard the snapshot without reverting (window elapsed or explicitly cancelled).
   */
  expire(): void {
    this._snapshot = null;
    this._remainingMs = 0;
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  /**
   * Per-frame: decrement window, auto-expire if elapsed.
   * @param delta Frame delta in ms.
   */
  update(delta: number): void {
    if (!this._snapshot) return;
    this._remainingMs = Math.max(0, this._remainingMs - delta);
    if (this._remainingMs <= 0) {
      this.expire();
    }
  }

  /** Release all state. */
  destroy(): void {
    this._snapshot = null;
    this._windowMs = 0;
    this._remainingMs = 0;
  }
}
